import React from 'react';
import { 
  Button, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Space, 
  Statistic,
  Tag,
  Carousel,
  Avatar
} from 'antd';
import {
  RocketOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  DashboardOutlined,
  StarOutlined,
  PlayCircleOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  CustomerServiceOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PublicHeader from '../../components/Layout/PublicHeader';
import './HomePage.css';

const { Title, Paragraph, Text } = Typography;

const HomePage: React.FC = () => {
  const navigate = useNavigate();


  const features = [
    {
      icon: <RocketOutlined />,
      title: '极速部署',
      description: '一键部署，秒级启动，让您的游戏服务器瞬间上线',
      color: 'var(--primary-color)'
    },
    {
      icon: <ThunderboltOutlined />,
      title: '高性能',
      description: '采用最新硬件配置，确保游戏运行流畅无卡顿',
      color: 'var(--warning-color)'
    },
    {
      icon: <SafetyOutlined />,
      title: '安全稳定',
      description: '99.9% 在线率保证，数据安全，服务稳定可靠',
      color: 'var(--success-color)'
    },
    {
      icon: <DashboardOutlined />,
      title: '独家面板',
      description: '悠悠鸟自主研发的游戏控制面板，针对每个游戏定制专属的管理信息，专业且易用',
      color: 'var(--accent-color)'
    }
  ];

  const stats = [
    { title: '在线服务器', value: 1250, suffix: '+', color: 'var(--primary-color)' },
    { title: '活跃用户', value: 15000, suffix: '+', color: 'var(--success-color)' },
    { title: '游戏类型', value: 25, suffix: '+', color: 'var(--warning-color)' },
    { title: '服务时长', value: 24, suffix: '/7', color: 'var(--accent-color)' }
  ];

  const testimonials = [
    {
      name: '游戏玩家小王',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=1',
      content: '服务器性能超棒，延迟很低，和朋友一起玩得很开心！',
      rating: 5
    },
    {
      name: '服务器管理员',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=2',
      content: '管理界面很直观，功能齐全，运维效率大大提升。',
      rating: 5
    },
    {
      name: '游戏开发者',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=3',
      content: 'API接口完善，文档详细，集成开发非常方便。',
      rating: 5
    }
  ];

  const games = [
    { name: 'Minecraft', players: '1000+', icon: '🎮' },
    { name: 'ARK', players: '500+', icon: '🦕' },
    { name: 'Rust', players: '800+', icon: '🦀' },
    { name: 'CS2', players: '1200+', icon: '🔫' },
    { name: 'Valheim', players: '300+', icon: '⚔️' },
    { name: '7 Days', players: '400+', icon: '🧟' }
  ];

  return (
    <div className="homepage-container">
      {/* 公共页头 */}
      <PublicHeader currentPage="home" />

      {/* 英雄区域 */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-particles"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-text">
            <Title level={1} className="hero-title">
              <span className="gradient-text">专业游戏服务器</span>
              <br />
              <span className="highlight-text">租赁平台</span>
            </Title>
            <Paragraph className="hero-description">
              提供高性能、低延迟的游戏服务器租赁服务，支持多种热门游戏，
              <br />
              悠悠鸟自主研发的游戏控制面板，针对不同游戏定制管理界面，美观，专业且更加易于使用。
              <br />
              <br />
              让您和朋友们享受流畅的游戏体验！
            </Paragraph>
            <Space size="large" className="hero-actions">
              <Button 
                type="primary" 
                size="large" 
                icon={<RocketOutlined />}
                onClick={() => navigate('/servers/create')}
                className="cta-button"
              >
                立即创建服务器
              </Button>
              <Button 
                size="large" 
                icon={<PlayCircleOutlined />}
                className="demo-button"
              >
                观看演示
              </Button>
            </Space>
          </div>
          <div className="hero-visual">
            <div className="floating-card">
              <div className="card-header">
                <div className="status-indicator online"></div>
                <Text strong>服务器状态</Text>
              </div>
              <div className="server-stats">
                <div className="stat-item">
                  <Text type="secondary">CPU使用率</Text>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '65%' }}></div>
                  </div>
                  <Text>65%</Text>
                </div>
                <div className="stat-item">
                  <Text type="secondary">内存使用</Text>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '42%' }}></div>
                  </div>
                  <Text>42%</Text>
                </div>
                <div className="stat-item">
                  <Text type="secondary">在线玩家</Text>
                  <Text strong style={{ color: 'var(--success-color)' }}>24/32</Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 统计数据 */}
      <section className="stats-section">
        <div className="container">
          <Row gutter={[32, 32]}>
            {stats.map((stat, index) => (
              <Col xs={12} sm={6} key={index}>
                <Card className="stat-card" bordered={false}>
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    suffix={stat.suffix}
                    valueStyle={{ color: stat.color, fontSize: '2rem', fontWeight: 'bold' }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 核心功能 */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <Title level={2} className="section-title">
              为什么选择我们
            </Title>
            <Paragraph className="section-description">
              我们致力于为玩家提供最优质的游戏服务器体验
            </Paragraph>
          </div>
          <Row gutter={[32, 32]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card 
                  className="feature-card" 
                  bordered={false}
                  hoverable
                >
                  <div className="feature-icon" style={{ color: feature.color }}>
                    {feature.icon}
                  </div>
                  <Title level={4} className="feature-title">
                    {feature.title}
                  </Title>
                  <Paragraph className="feature-description">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 热门游戏 */}
      <section className="games-section">
        <div className="container">
          <div className="section-header">
            <Title level={2} className="section-title">
              支持的热门游戏
            </Title>
            <Paragraph className="section-description">
              覆盖主流游戏类型，满足不同玩家需求
            </Paragraph>
          </div>
          <Row gutter={[16, 16]}>
            {games.map((game, index) => (
              <Col xs={12} sm={8} md={4} key={index}>
                <Card 
                  className="game-card" 
                  bordered={false}
                  hoverable
                >
                  <div className="game-icon">{game.icon}</div>
                  <Title level={5} className="game-name">
                    {game.name}
                  </Title>
                  <Text type="secondary" className="game-players">
                    {game.players} 在线
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 用户评价 */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <Title level={2} className="section-title">
              用户评价
            </Title>
            <Paragraph className="section-description">
              听听用户们怎么说
            </Paragraph>
          </div>
          <Carousel 
            autoplay 
            dots={false}
            effect="fade"
            className="testimonials-carousel"
          >
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-slide">
                <Card className="testimonial-card" bordered={false}>
                  <div className="testimonial-content">
                    <div className="stars">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <StarOutlined key={i} style={{ color: '#faad14' }} />
                      ))}
                    </div>
                    <Paragraph className="testimonial-text">
                      "{testimonial.content}"
                    </Paragraph>
                    <div className="testimonial-author">
                      <Avatar src={testimonial.avatar} size={48} />
                      <div className="author-info">
                        <Text strong>{testimonial.name}</Text>
                        <Text type="secondary">游戏玩家</Text>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </Carousel>
        </div>
      </section>

      {/* 服务优势 */}
      <section className="advantages-section">
        <div className="container">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <div className="advantages-content">
                <Title level={2} className="section-title">
                  专业服务团队
                </Title>
                <Paragraph className="section-description">
                  我们拥有经验丰富的技术团队，7x24小时为您提供专业的技术支持
                </Paragraph>
                <div className="advantage-list">
                  <div className="advantage-item">
                    <CheckCircleOutlined className="advantage-icon" />
                    <div>
                      <Text strong>24/7 技术支持</Text>
                      <br />
                      <Text type="secondary">全天候在线，随时解决您的问题</Text>
                    </div>
                  </div>
                  <div className="advantage-item">
                    <CheckCircleOutlined className="advantage-icon" />
                    <div>
                      <Text strong>快速响应</Text>
                      <br />
                      <Text type="secondary">平均响应时间 &lt; 5 分钟</Text>
                    </div>
                  </div>
                  <div className="advantage-item">
                    <CheckCircleOutlined className="advantage-icon" />
                    <div>
                      <Text strong>专业指导</Text>
                      <br />
                      <Text type="secondary">提供详细的服务器配置建议</Text>
                    </div>
                  </div>
                </div>
                <Button 
                  type="primary" 
                  size="large"
                  onClick={() => navigate('/pricing')}
                  className="cta-button"
                >
                  查看价格方案
                  <ArrowRightOutlined />
                </Button>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className="advantages-visual">
                <div className="support-card">
                  <div className="support-header">
                    <CustomerServiceOutlined className="support-icon" />
                    <Text strong>在线客服</Text>
                    <Tag color="green" className="status-tag">在线</Tag>
                  </div>
                  <div className="support-messages">
                    <div className="message user-message">
                      <Text>服务器突然卡顿怎么办？</Text>
                    </div>
                    <div className="message support-message">
                      <Text>我来帮您检查一下服务器状态...</Text>
                    </div>
                    <div className="message support-message">
                      <Text>已为您重启服务器，现在应该正常了</Text>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* CTA 区域 */}
      <section className="cta-section">
        <div className="container">
          <Card className="cta-card" bordered={false}>
            <div className="cta-content">
              <Title level={2} className="cta-title">
                准备开始您的游戏之旅？
              </Title>
              <Paragraph className="cta-description">
                立即创建您的专属游戏服务器，与朋友们一起享受游戏乐趣
              </Paragraph>
              <Space size="large">
                <Button 
                  type="primary" 
                  size="large"
                  onClick={() => navigate('/servers/create')}
                  className="cta-button"
                >
                  免费试用
                </Button>
              </Space>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
